import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { redirectToOkta } from 'utils/app.utils';
import { useLocation } from 'react-router-dom';
import { POLL_DURATION } from 'constants/AppConstants';
import Shimmer from 'commonComponents/Loader/Shimmer.component';
import useInterval from 'commonComponents/customHooks/useInterval.hook';
import get from 'lodash/get';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as actionCreators from './Authorize.actions';
import AutoProvision from './autoProvision/AutoProvision.component';
import AutoProvisionFailed from './autoProvision/AutoProvisionFailed.component';
import AutoProvisionSignUp from './autoProvision/AutoProvisionSignUp.component';

/**
 * Authorize component - handles okta integration
 */
const Authorize = (props) => {
  const {
    userOnboardStatus: {
      userCreatedInAMP = false,
      userCreatedInLegacy = false,
      isUserFederated = false,
    },
    maxPollTime = 0,
    showToast = false,
    autoProvisionFlag = false,
    autoProvisionStatus,
    actions: {
      getAccessTokenAction, getUserOnboardingStatus, setAutoProvisionFlag, setAutoProvisionStatus, getNewAccessTokenAction,
      setAuthorize,
    },
  } = props;
  const authCoderef = useRef();

  /**
   * returns the current location object.
   */
  const [progress, setProgress] = useState(10);

  const location = useLocation();
  const { search, state: { subRoute = '/' } = {} } = location;
  const flags = useFlags();

  // if code value is present, dispatch OKTA_TOKEN_REQUESTED to exchange code with token, else redirects to okta
  useEffect(() => {
    // parse url to get code and state values
    setAutoProvisionFlag(get(flags, 'autoProvisioning', false));
    const urlSearchParams = new URLSearchParams(search);
    const authCode = urlSearchParams.get('code');
    const state = urlSearchParams.get('state');
    if (authCode && authCoderef.current !== authCode) {
      getAccessTokenAction(authCode, state);
      authCoderef.current = authCode;
    } else {
      authCoderef.current = null;
      // Don't redirect to Okta if the subRoute is logout-handler
      if (subRoute !== '/logout-handler') {
        redirectToOkta(subRoute);
      }
    }
  }, []);

  useEffect(() => {
    if (userCreatedInAMP) {
      setProgress(100);
    }
  }, [userCreatedInAMP]);

  useEffect(() => {
    if (autoProvisionFlag && autoProvisionStatus.length > 0) {
      if (userCreatedInAMP && maxPollTime > 0 && !showToast) {
        setAutoProvisionStatus('COMPLETE');
        getNewAccessTokenAction(true, subRoute);
        setAuthorize();
      } else if (!userCreatedInAMP && (maxPollTime <= 0 || showToast)) {
        setAutoProvisionStatus('FAILED');
      } else if (!userCreatedInAMP && !userCreatedInLegacy && !isUserFederated) {
        setAutoProvisionStatus('SIGN_UP');
      } else {
        setAutoProvisionStatus('PENDING');
      }
    }
  }, [userCreatedInAMP, userCreatedInLegacy, isUserFederated, maxPollTime, showToast]);

  const pollingApi = useCallback(() => {
    if (autoProvisionFlag && autoProvisionStatus === 'PENDING' && !userCreatedInAMP && maxPollTime > 0) {
      getUserOnboardingStatus();
      setProgress((prevProgress) => {
        let prg = prevProgress;
        if (prevProgress < 90) {
          prg = prevProgress + 10;
        } else if (prevProgress >= 100) {
          prg = 10;
        }
        return prg;
      });
    }
  }, [userCreatedInAMP, maxPollTime, autoProvisionStatus]);

  useInterval(pollingApi, POLL_DURATION);

  const authorizeMessage = useCallback(() => {
    switch (autoProvisionStatus) {
      case 'PENDING':
        return (<AutoProvision progress={progress} />);
      case 'FAILED':
        return (<AutoProvisionFailed />);
      case 'SIGN_UP':
        return (<AutoProvisionSignUp />);
      default:
        return (<Shimmer appName="" />);
    }
  }, [autoProvisionStatus, progress]);

  return (authorizeMessage());
};

/**
 * returns a set of actions after binding dispatch
 * @param {Function} dispatch - redux dispatch function
 */
const actions = (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
});

Authorize.propTypes = {
  /**
   * set of actions passed by actions function
   */
  actions: PropTypes.shape({
    getAccessTokenAction: PropTypes.func.isRequired,
    getUserOnboardingStatus: PropTypes.func.isRequired,
    setAutoProvisionFlag: PropTypes.func.isRequired,
    setAutoProvisionStatus: PropTypes.func.isRequired,
    setAuthorize: PropTypes.func.isRequired,
  }).isRequired,

  /**
     * @property {Number} maxPollTime - maximum poll duration.
     */
  maxPollTime: PropTypes.number.isRequired,
  /**
   * @property {Object} userOnboardStatus - user onboard status.
   */
  userOnboardStatus: PropTypes.shape({
    userCreatedInAMP: PropTypes.bool.isRequired,
  }).isRequired,
  /**
     * @property {Bool} showToast - show error toast message.
     */
  showToast: PropTypes.bool.isRequired,
  /**
     * @property {Bool} authorize - show if user is authorized.
     */
  authorize: PropTypes.bool.isRequired,
  /**
     * @property {Bool} autoProvisionFlag - auto provision feature flag.
     */
  autoProvisionFlag: PropTypes.bool.isRequired,
  /**
     * @property {String} autoProvisionStatus - auto provision feature flag.
     */
  autoProvisionStatus: PropTypes.string.isRequired,
};

/**
 * selects required data from redux store
 * @param {Object} state - redux store object
 */
const selector = (state) => ({
  userId: state.Authorize.userId,
  maxPollTime: state.Authorize.maxPollTime,
  showToast: state.Authorize.showToast,
  userOnboardStatus: state.Authorize.userOnboardStatus,
  autoProvisionFlag: state.Authorize.autoProvisionFlag,
  autoProvisionStatus: state.Authorize.autoProvisionStatus,

});

export default connect(selector, actions)(Authorize);
